import {
  IBrand,
  INBOUND_MESSAGE_PATIENT_STATUSES,
  IPatient,
  PatientStatus,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  IReffable,
  multiFilter,
  multiSwitchMap,
  snapshot,
  where,
  WithRef,
} from '@principle-theorem/shared';
import { first, sortBy } from 'lodash';
import { Brand } from '../brand';

/**
 * Resolves which patient an inbound message came from, given the phone number
 * that sent it.
 *
 * A brand can hold the same phone number against several patients - families
 * routinely share one mobile - so matching has to both exclude patients the
 * practice does not want to hear from and rank whoever is left.
 */
export class InboundMessagePatient {
  /**
   * Every patient the phone number resolves to, best match first.
   *
   * Prefer `findBestMatch` unless you need to disambiguate between patients
   * yourself.
   */
  static async findMatches(
    brand: IReffable<IBrand>,
    phoneNumber: string
  ): Promise<WithRef<IPatient>[]> {
    const matches = await snapshot(
      Firestore.query$(
        Brand.patientPhoneNumberCol(brand),
        where('phoneNumber', '==', phoneNumber)
      ).pipe(
        multiSwitchMap((patientPhoneNumber) =>
          Firestore.getDoc(patientPhoneNumber.patientRef)
        ),
        multiFilter((patient) =>
          InboundMessagePatient._canMatch(patient.status)
        )
      )
    );

    return sortBy(matches, (patient) =>
      InboundMessagePatient._matchPriority(patient.status)
    );
  }

  /**
   * The single most plausible sender of an inbound message, or `undefined`
   * when the phone number belongs to nobody the practice wants to hear from.
   */
  static async findBestMatch(
    brand: IReffable<IBrand>,
    phoneNumber: string
  ): Promise<WithRef<IPatient> | undefined> {
    const matches = await InboundMessagePatient.findMatches(brand, phoneNumber);

    // TODO: https://app.clickup.com/t/370fyp7 - Handle multiple patients
    return first(matches);
  }

  private static _canMatch(status: PatientStatus): boolean {
    return INBOUND_MESSAGE_PATIENT_STATUSES.includes(status);
  }

  private static _matchPriority(status: PatientStatus): number {
    return INBOUND_MESSAGE_PATIENT_STATUSES.indexOf(status);
  }
}
